import { registerIndicator, type KLineData } from 'klinecharts'

import { type IndicatorSpec, registerIndicatorSpecs, registerOnce } from './registrationUtils'
import {
  type Num,
  type OhlcvBar,
  rollingMean,
  wmaSeries,
  emaSeries,
  wilderSeries,
  trueRangeSeries,
} from './seriesMath'

/**
 * Extended moving-average family + price bands/channels (main-pane overlays):
 *
 *   MAs:   WMA, HMA, DEMA, TEMA, TMA (triangular), KAMA, VIDYA, ZLEMA,
 *          MGD (McGinley Dynamic), WWS (Welles Wilder Smoothing)
 *   Bands: KC (Keltner), DC (Donchian), ENV (MA Envelopes),
 *          HLB (High Low Bands), APZ (Adaptive Price Zone)
 *
 * The single-line MAs are duplicable (stackable) via maInstances — see
 * MA_CLONE_CALCS at the bottom.
 */

/* ------------------------------------------------------------------ */
/*  Moving averages                                                     */
/* ------------------------------------------------------------------ */

/** WMA — linearly weighted moving average of close. */
export function computeWMA(bars: readonly OhlcvBar[], period: number): Array<{ wma?: number }> {
  const wma = wmaSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => ({ wma: wma[i] }))
}

/** HMA — Hull MA: WMA(2·WMA(p/2) − WMA(p), √p). Zero lag on linear trends. */
export function computeHMA(bars: readonly OhlcvBar[], period: number): Array<{ hma?: number }> {
  const closes = bars.map((b) => b.close)
  const half = wmaSeries(closes, Math.max(1, Math.floor(period / 2)))
  const full = wmaSeries(closes, period)
  const diff = bars.map((_, i) =>
    half[i] != null && full[i] != null ? 2 * half[i]! - full[i]! : undefined,
  )
  const hma = wmaSeries(diff, Math.max(1, Math.floor(Math.sqrt(period))))
  return bars.map((_, i) => ({ hma: hma[i] }))
}

/** DEMA — 2·EMA − EMA(EMA). */
export function computeDEMA(bars: readonly OhlcvBar[], period: number): Array<{ dema?: number }> {
  const e1 = emaSeries(
    bars.map((b) => b.close),
    period,
  )
  const e2 = emaSeries(e1, period)
  return bars.map((_, i) => ({
    dema: e1[i] != null && e2[i] != null ? 2 * e1[i]! - e2[i]! : undefined,
  }))
}

/** TEMA — 3·EMA − 3·EMA(EMA) + EMA(EMA(EMA)). */
export function computeTEMA(bars: readonly OhlcvBar[], period: number): Array<{ tema?: number }> {
  const e1 = emaSeries(
    bars.map((b) => b.close),
    period,
  )
  const e2 = emaSeries(e1, period)
  const e3 = emaSeries(e2, period)
  return bars.map((_, i) => ({
    tema:
      e1[i] != null && e2[i] != null && e3[i] != null
        ? 3 * e1[i]! - 3 * e2[i]! + e3[i]!
        : undefined,
  }))
}

/** Triangular MA of an arbitrary series (TA-Lib period split). */
function trimaSeries(values: readonly Num[], period: number): Num[] {
  const p1 = period % 2 === 1 ? (period + 1) / 2 : period / 2
  const p2 = period % 2 === 1 ? (period + 1) / 2 : period / 2 + 1
  return rollingMean(rollingMean(values, p1), p2)
}

/** TMA — triangular moving average (double-smoothed SMA). */
export function computeTMA(bars: readonly OhlcvBar[], period: number): Array<{ tma?: number }> {
  const tma = trimaSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => ({ tma: tma[i] }))
}

/** KAMA — Kaufman adaptive MA (efficiency-ratio alpha between EMA-2 and EMA-30). */
export function computeKAMA(bars: readonly OhlcvBar[], period: number): Array<{ kama?: number }> {
  const closes = bars.map((b) => b.close)
  const fastSC = 2 / (2 + 1)
  const slowSC = 2 / (30 + 1)
  const out: Num[] = new Array(bars.length).fill(undefined)
  let kama: number | undefined
  for (let i = period; i < bars.length; i++) {
    if (kama === undefined) kama = closes[i - 1]
    let noise = 0
    for (let j = i - period + 1; j <= i; j++) noise += Math.abs(closes[j] - closes[j - 1])
    const er = noise > 0 ? Math.abs(closes[i] - closes[i - period]) / noise : 0
    const sc = (er * (fastSC - slowSC) + slowSC) ** 2
    kama = kama + sc * (closes[i] - kama)
    out[i] = kama
  }
  return bars.map((_, i) => ({ kama: out[i] }))
}

/** VIDYA — Chande's variable dynamic average; EMA alpha scaled by |CMO|. */
export function computeVIDYA(
  bars: readonly OhlcvBar[],
  period: number,
  cmoPeriod: number,
): Array<{ vidya?: number }> {
  const closes = bars.map((b) => b.close)
  const alpha = 2 / (period + 1)
  const out: Num[] = new Array(bars.length).fill(undefined)
  let vidya: number | undefined
  for (let i = cmoPeriod; i < bars.length; i++) {
    let up = 0
    let down = 0
    for (let j = i - cmoPeriod + 1; j <= i; j++) {
      const diff = closes[j] - closes[j - 1]
      if (diff > 0) up += diff
      else down -= diff
    }
    const k = up + down > 0 ? Math.abs(up - down) / (up + down) : 0
    if (vidya === undefined) vidya = closes[i]
    else vidya = closes[i] * alpha * k + vidya * (1 - alpha * k)
    out[i] = vidya
  }
  return bars.map((_, i) => ({ vidya: out[i] }))
}

/** ZLEMA — zero-lag EMA: EMA of (2·close − close[lag]) with lag=(p−1)/2. */
export function computeZLEMA(bars: readonly OhlcvBar[], period: number): Array<{ zlema?: number }> {
  const closes = bars.map((b) => b.close)
  const lag = Math.floor((period - 1) / 2)
  const adjusted = closes.map((c, i) => (i >= lag ? 2 * c - closes[i - lag] : undefined))
  const zlema = emaSeries(adjusted, period)
  return bars.map((_, i) => ({ zlema: zlema[i] }))
}

/** MGD — McGinley Dynamic: MD += (close − MD) / (0.6·p·(close/MD)⁴). */
export function computeMGD(bars: readonly OhlcvBar[], period: number): Array<{ mgd?: number }> {
  const closes = bars.map((b) => b.close)
  const out: Num[] = new Array(bars.length).fill(undefined)
  let md: number | undefined
  let seedSum = 0
  for (let i = 0; i < bars.length; i++) {
    if (md === undefined) {
      seedSum += closes[i]
      if (i === period - 1) {
        md = seedSum / period
        out[i] = md
      }
      continue
    }
    if (md <= 0) md = closes[i]
    else md = md + (closes[i] - md) / (0.6 * period * (closes[i] / md) ** 4)
    out[i] = md
  }
  return bars.map((_, i) => ({ mgd: out[i] }))
}

/** WWS — Welles Wilder Smoothing of close (the RSI/ATR smoothing as an MA). */
export function computeWWS(bars: readonly OhlcvBar[], period: number): Array<{ wws?: number }> {
  const wws = wilderSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => ({ wws: wws[i] }))
}

/* ------------------------------------------------------------------ */
/*  Bands / channels                                                    */
/* ------------------------------------------------------------------ */

type Band = { up?: number; mid?: number; dn?: number }

/** KC — Keltner Channel: EMA midline ± mult · ATR. */
export function computeKC(
  bars: readonly OhlcvBar[],
  period: number,
  atrPeriod: number,
  mult: number,
): Band[] {
  const mid = emaSeries(
    bars.map((b) => b.close),
    period,
  )
  const atr = wilderSeries(trueRangeSeries(bars), atrPeriod)
  return bars.map((_, i) => {
    if (mid[i] == null || atr[i] == null) return {}
    return { up: mid[i]! + mult * atr[i]!, mid: mid[i], dn: mid[i]! - mult * atr[i]! }
  })
}

/** DC — Donchian Channel: highest high / lowest low over the period. */
export function computeDC(bars: readonly OhlcvBar[], period: number): Band[] {
  return bars.map((_, i) => {
    if (i < period - 1) return {}
    let hh = -Infinity
    let ll = Infinity
    for (let j = i - period + 1; j <= i; j++) {
      if (bars[j].high > hh) hh = bars[j].high
      if (bars[j].low < ll) ll = bars[j].low
    }
    return { up: hh, mid: (hh + ll) / 2, dn: ll }
  })
}

/** ENV — MA Envelopes: SMA midline ± percent. */
export function computeENV(bars: readonly OhlcvBar[], period: number, percent: number): Band[] {
  const mid = rollingMean(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => {
    if (mid[i] == null) return {}
    return { up: mid[i]! * (1 + percent / 100), mid: mid[i], dn: mid[i]! * (1 - percent / 100) }
  })
}

/** HLB — High Low Bands: triangular MAs of high and low. */
export function computeHLB(bars: readonly OhlcvBar[], period: number): Band[] {
  const up = trimaSeries(
    bars.map((b) => b.high),
    period,
  )
  const dn = trimaSeries(
    bars.map((b) => b.low),
    period,
  )
  return bars.map((_, i) => {
    if (up[i] == null || dn[i] == null) return {}
    return { up: up[i], mid: (up[i]! + dn[i]!) / 2, dn: dn[i] }
  })
}

/** APZ — Adaptive Price Zone: double-EMA midline ± band · double-EMA of range. */
export function computeAPZ(bars: readonly OhlcvBar[], period: number, band: number): Band[] {
  const n = Math.max(1, Math.ceil(Math.sqrt(period)))
  const mid = emaSeries(
    emaSeries(
      bars.map((b) => b.close),
      n,
    ),
    n,
  )
  const range = emaSeries(
    emaSeries(
      bars.map((b) => b.high - b.low),
      n,
    ),
    n,
  )
  return bars.map((_, i) => {
    if (mid[i] == null || range[i] == null) return {}
    return { up: mid[i]! + band * range[i]!, mid: mid[i], dn: mid[i]! - band * range[i]! }
  })
}

/* ------------------------------------------------------------------ */
/*  Registration                                                        */
/* ------------------------------------------------------------------ */

const maFigure = (key: string, title: string) => [{ key, title, type: 'line' as const }]
const bandFigures = [
  { key: 'up', title: 'UP: ', type: 'line' as const },
  { key: 'mid', title: 'MID: ', type: 'line' as const },
  { key: 'dn', title: 'DN: ', type: 'line' as const },
]

const SPECS: IndicatorSpec[] = [
  {
    name: 'WMA',
    shortName: 'WMA',
    calcParams: [20],
    figures: maFigure('wma', 'WMA: '),
    calc: (d, [p]) => computeWMA(d, p),
  },
  {
    name: 'HMA',
    shortName: 'HMA',
    calcParams: [16],
    figures: maFigure('hma', 'HMA: '),
    calc: (d, [p]) => computeHMA(d, p),
  },
  {
    name: 'DEMA',
    shortName: 'DEMA',
    calcParams: [20],
    figures: maFigure('dema', 'DEMA: '),
    calc: (d, [p]) => computeDEMA(d, p),
  },
  {
    name: 'TEMA',
    shortName: 'TEMA',
    calcParams: [20],
    figures: maFigure('tema', 'TEMA: '),
    calc: (d, [p]) => computeTEMA(d, p),
  },
  {
    name: 'TMA',
    shortName: 'TMA',
    calcParams: [20],
    figures: maFigure('tma', 'TMA: '),
    calc: (d, [p]) => computeTMA(d, p),
  },
  {
    name: 'KAMA',
    shortName: 'KAMA',
    calcParams: [10],
    figures: maFigure('kama', 'KAMA: '),
    calc: (d, [p]) => computeKAMA(d, p),
  },
  {
    name: 'VIDYA',
    shortName: 'VIDYA',
    calcParams: [14, 9],
    figures: maFigure('vidya', 'VIDYA: '),
    calc: (d, [p, c]) => computeVIDYA(d, p, c),
  },
  {
    name: 'ZLEMA',
    shortName: 'ZLEMA',
    calcParams: [14],
    figures: maFigure('zlema', 'ZLEMA: '),
    calc: (d, [p]) => computeZLEMA(d, p),
  },
  {
    name: 'MGD',
    shortName: 'MGD',
    calcParams: [14],
    figures: maFigure('mgd', 'MGD: '),
    calc: (d, [p]) => computeMGD(d, p),
  },
  {
    name: 'WWS',
    shortName: 'WWS',
    calcParams: [14],
    figures: maFigure('wws', 'WWS: '),
    calc: (d, [p]) => computeWWS(d, p),
  },
  {
    name: 'KC',
    shortName: 'KC',
    calcParams: [20, 10, 2],
    figures: bandFigures,
    calc: (d, [p, a, m]) => computeKC(d, p, a, m),
  },
  {
    name: 'DC',
    shortName: 'DC',
    calcParams: [20],
    figures: bandFigures,
    calc: (d, [p]) => computeDC(d, p),
  },
  {
    name: 'ENV',
    shortName: 'ENV',
    calcParams: [20, 2.5],
    figures: bandFigures,
    calc: (d, [p, pct]) => computeENV(d, p, pct),
  },
  {
    name: 'HLB',
    shortName: 'HLB',
    calcParams: [14],
    figures: bandFigures,
    calc: (d, [p]) => computeHLB(d, p),
  },
  {
    name: 'APZ',
    shortName: 'APZ',
    calcParams: [21, 2],
    figures: bandFigures,
    calc: (d, [p, b]) => computeAPZ(d, p, b),
  },
]

/** Names of every indicator this module registers (used by pickers/tests). */
export const MA_BAND_INDICATOR_NAMES = SPECS.map((s) => s.name)

/** Register the extended MA family + bands with klinecharts (idempotent). */
export const registerMaBandIndicators = registerOnce(() => {
  // Everything in this catalog is a main-pane (price-scale) overlay.
  registerIndicatorSpecs(registerIndicator, SPECS, { series: 'price', precision: 2 })
})

/* ------------------------------------------------------------------ */
/*  Duplicable-instance support (maInstances)                           */
/* ------------------------------------------------------------------ */

type CloneCalc = (
  dataList: KLineData[],
  indicator: { calcParams?: number[] },
) => Record<string, number>[]

function cloneCalc(
  compute: (bars: KLineData[], params: number[]) => Array<Record<string, number | undefined>>,
  defaults: number[],
): (key: string) => CloneCalc {
  return (key: string) => (dataList, indicator) => {
    const params = (indicator.calcParams as number[] | undefined) ?? defaults
    return compute(dataList, params).map((v) => {
      const value = Object.values(v)[0]
      return value == null ? {} : { [key]: value }
    })
  }
}

/**
 * Calc factories for the single-line MAs so maInstances can register `WMA#2`,
 * `HMA#2`, … clones. Keyed by base name; the factory re-emits under the
 * catalog figure key of the clone.
 */
export const MA_CLONE_CALCS: Record<string, (key: string) => CloneCalc> = {
  WMA: cloneCalc((d, [p]) => computeWMA(d, p), [20]),
  HMA: cloneCalc((d, [p]) => computeHMA(d, p), [16]),
  DEMA: cloneCalc((d, [p]) => computeDEMA(d, p), [20]),
  TEMA: cloneCalc((d, [p]) => computeTEMA(d, p), [20]),
  TMA: cloneCalc((d, [p]) => computeTMA(d, p), [20]),
  KAMA: cloneCalc((d, [p]) => computeKAMA(d, p), [10]),
  VIDYA: cloneCalc((d, [p, c]) => computeVIDYA(d, p, c), [14, 9]),
  ZLEMA: cloneCalc((d, [p]) => computeZLEMA(d, p), [14]),
  MGD: cloneCalc((d, [p]) => computeMGD(d, p), [14]),
  WWS: cloneCalc((d, [p]) => computeWWS(d, p), [14]),
}
